import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import {
  View,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  ActivityIndicator,
  RefreshControl,
  Modal,
  TextInput,
  Animated,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { Text } from '../components/ui/AppText';
// `Alert` daqui, não do react-native: no navegador o Alert do RN é um stub
// vazio e engole tudo em silêncio (ver src/utils/appAlert.js). Em nativo é
// reexportação direta — comportamento do app inalterado.
import { Alert } from '../utils/appAlert';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
// Paleta derivada da cor-base #03386C (hue 210, sat 95%) em 3 luminosidades —
// mesma dos cabecalhos ja convertidos. Local por enquanto: o HEADER_GRADIENT
// global segue roxo, entao importa-lo aqui traria a cor antiga de volta.
const COVER_GRADIENT = ['#022445', '#03386C', '#01172D'];
import DottedBackground from '../components/DottedBackground';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import Ionicons from '@expo/vector-icons/Ionicons';
import Svg, { Circle } from 'react-native-svg';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { signOut, deleteAccount } from '../services/authService';
import {
  updateUserProfile,
  uploadProfilePhoto,
  deleteProfilePhoto,
} from '../services/databaseService';
import BenchmarkThermometerCard from '../components/Home/BenchmarkThermometerCard';
// React Query - Migração Fase 4 (fonte única de cache, persistido)
import { useQueryClient } from '@tanstack/react-query';
import {
  useProfileScreen,
  profileKeys,
} from '../hooks/queries/useProfileQuery';
import { useScreenQueryState } from '../hooks/useScreenQueryState';
import { UserContentList } from '../features/profileContent';
import { SkeletonCircle, SkeletonText } from '../components/SkeletonLoading';
import FeedbackModal from '../components/ui/FeedbackModal';

// 12 Seeds fixos para Fun Emoji (geram avatares únicos)
const FUN_EMOJI_SEEDS = [
  'happy', 'cool', 'star', 'love', 'fire', 'smile',
  'ninja', 'crown', 'rocket', 'diamond', 'magic', 'sun',
];

// Gera URL do avatar DiceBear Fun Emoji
const getDiceBearUrl = (seed, size = 120) => {
  return `https://api.dicebear.com/7.x/fun-emoji/png?seed=${encodeURIComponent(seed)}&size=${size}`;
};

// Componente de Avatar MEMOIZADO - evita re-render quando outros estados mudam
const ProfileAvatar = memo(({ fotoUrl, avatarSeed, style }) => {
  const avatarUri = fotoUrl || getDiceBearUrl(avatarSeed || 'happy', 100);

  return (
    <Image
      source={{ uri: avatarUri }}
      style={style}
      contentFit="cover"
      cachePolicy="memory-disk"
    />
  );
  // SEM `transition`/`placeholder`: o avatar fica atrás do <Modal transparent> de
  // edição; ao digitar, o Android redesenha a janela de trás e o expo-image
  // replayava o fade (transition) + flash do blurhash = "piscar". Cache memory-disk
  // mantém a imagem instantânea; o fade era só cosmético.
}, (prevProps, nextProps) => {
  // Só re-renderiza se a foto ou seed realmente mudou
  return prevProps.fotoUrl === nextProps.fotoUrl &&
    prevProps.avatarSeed === nextProps.avatarSeed;
});

// Preview da foto no modal de edição — MEMOIZADO pelo mesmo motivo do ProfileAvatar:
// digitar no nome/bio chama setEditForm → re-render da tela. Sem memo, o expo-image
// recebe um source novo a cada tecla e replaya a transição (= "piscar").
const EditPhotoPreview = memo(({ fotoUrl, avatarSeed }) => {
  const uri = fotoUrl || getDiceBearUrl(avatarSeed || 'happy', 80);
  return (
    <Image
      source={{ uri }}
      style={styles.photoPreviewImg}
      placeholder={{ blurhash: 'LGF5?xYk^6#M@-5c,1J5@[or[Q6.' }}
      contentFit="cover"
      transition={150}
      cachePolicy="memory-disk"
    />
  );
}, (prevProps, nextProps) =>
  prevProps.fotoUrl === nextProps.fotoUrl &&
  prevProps.avatarSeed === nextProps.avatarSeed);

// Item do seletor de avatar — MEMOIZADO: a URL do avatar é fixa por seed, então
// o expo-image nunca precisa recarregar/transicionar ao digitar o nome. Só
// re-renderiza quando muda a seleção (onSelect precisa ser estável via useCallback).
const AvatarGridItem = memo(({ seed, selected, onSelect, bgColor }) => (
  <TouchableOpacity
    style={[styles.avatarGridItem, { backgroundColor: bgColor }, selected && styles.avatarGridItemSelected]}
    onPress={() => onSelect(seed)}
  >
    <Image
      source={{ uri: getDiceBearUrl(seed, 60) }}
      style={styles.avatarGridImg}
      placeholder={{ blurhash: 'LGF5?xYk^6#M@-5c,1J5@[or[Q6.' }}
      contentFit="cover"
      transition={100}
      cachePolicy="memory-disk"
    />
  </TouchableOpacity>
));

const PROFILE_WEEKLY_RING_SIZE = 80;
const PROFILE_WEEKLY_RING_STROKE = 6;
const PROFILE_WEEKLY_RING_RADIUS = (PROFILE_WEEKLY_RING_SIZE - PROFILE_WEEKLY_RING_STROKE) / 2;
const PROFILE_WEEKLY_RING_CIRCUMFERENCE = 2 * Math.PI * PROFILE_WEEKLY_RING_RADIUS;

// "Membro desde" no mesmo formato do service (mapProfileScreenPayload), para o
// valor pré-preenchido do AuthContext bater com o que a query traz depois.
const formatMemberSince = (createdAt) => {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
};

// Dados fallback do usuário (usados enquanto carrega)
// `memberSince: null` de propósito: era 'Dez 2024' fixo, então conta criada
// hoje exibia essa data inventada. Sem valor real, a linha não é renderizada.
const DEFAULT_USER_DATA = {
  name: 'Usuário',
  username: 'user',
  avatarSeed: 'happy',
  bio: 'Estudante de concursos públicos focado na aprovação! 🚀',
  isVerified: false,
  isPremium: false,
  memberSince: null,
  status: 'online',
  streak: {
    current: 0,
    progress: 0,
  },
};

// Estatísticas fallback
const DEFAULT_STATS = {
  questionsAnswered: 0,
  accuracyRate: 0,
  totalStudyTime: '0h 0m',
  globalRank: 0,
  rankChange: 0,
  followers: 0,
  achievements: 0,
};

const DEFAULT_WEEKLY_STATS = {
  total: 0,
  correct: 0,
  wrong: 0,
  percentage: 0,
};

// Conquistas fallback — precisam de progress/current/target: conta sem conquista
// no banco fica NELAS (a sincronização só substitui se vier length > 0) e o card
// bloqueado renderiza `${progress}%` e `current/target` (sem os campos virava
// width "undefined%" e o texto "/" solto).
const DEFAULT_ACHIEVEMENTS = [
  { id: '1', icon: '🏆', name: 'Primeira Vitória', desc: 'Acertou sua primeira questão', unlocked: false, rarity: 'common', progress: 0, current: 0, target: 1 },
  { id: '2', icon: '💎', name: 'Perfeição', desc: '100% em 50 questões', unlocked: false, rarity: 'rare', progress: 0, current: 0, target: 50 },
  { id: '3', icon: '🔥', name: '30 Dias Seguidos', desc: 'Sequência de 30 dias', unlocked: false, rarity: 'epic', progress: 0, current: 0, target: 30 },
];

// Atividades fallback
const DEFAULT_ACTIVITIES = [
  { id: '1', type: 'quiz', icon: '✅', text: 'Comece a estudar para ver suas atividades!', time: 'agora', badge: null },
];

// Próximo objetivo fallback
const DEFAULT_NEXT_GOAL = {
  icon: '🎯',
  name: 'Primeiro Passo',
  desc: 'Responda sua primeira questão',
  current: 0,
  target: 1,
};

import { getColors } from './ProfileScreen.colors';
import { styles } from './ProfileScreen.styles';

// Cores padrão para StyleSheet (serão sobrescritas dinamicamente)
const colors = getColors(true);

const ProfileScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { isAuthenticated, profile, user, refreshProfile } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  // Cores dinâmicas baseadas no tema atual
  const dynamicColors = useMemo(() => getColors(isDarkMode), [isDarkMode]);


  // Estados para dados do banco - usa profile do auth context como inicial para evitar flash
  const [userData, setUserData] = useState(() => ({
    ...DEFAULT_USER_DATA,
    name: profile?.nome || DEFAULT_USER_DATA.name,
    username: profile?.username || DEFAULT_USER_DATA.username,
    fotoUrl: profile?.foto_url || null,
    avatarSeed: profile?.avatar_seed || DEFAULT_USER_DATA.avatarSeed,
    bio: profile?.bio || DEFAULT_USER_DATA.bio,
    isPremium: profile?.is_premium || false,
    isVerified: profile?.is_verified || false,
    // O AuthContext já carrega `created_at` (PROFILE_PUBLIC_COLUMNS) → dá pra
    // mostrar a data real desde o boot, sem esperar a query da tela.
    memberSince: formatMemberSince(profile?.created_at),
  }));
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [weeklyStats, setWeeklyStats] = useState(DEFAULT_WEEKLY_STATS);
  const [achievements, setAchievements] = useState(DEFAULT_ACHIEVEMENTS);
  const [activities, setActivities] = useState(DEFAULT_ACTIVITIES);
  const [nextGoal, setNextGoal] = useState(DEFAULT_NEXT_GOAL);
  const [refreshing, setRefreshing] = useState(false);

  // Estados para o modal de edição
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  // ── Entrada/saída do modal de edição (mesmo padrão do CreatePostModal) ──
  // Com `animationType="slide"` do RN quem desliza é o modal INTEIRO — e como o
  // escurecimento era o próprio container (`modalOverlay` tinha o fundo preto),
  // ele subia junto com a folha, escurecendo a tela de baixo para cima. Aqui a
  // camada escura faz fade PARADA no lugar e só a folha desliza.
  const { height: windowHeight } = useWindowDimensions();
  const editSlideAnim = useRef(new Animated.Value(windowHeight)).current;
  const editFadeAnim = useRef(new Animated.Value(0)).current;
  // `animationType="none"` ⇒ o Modal precisa continuar montado durante a SAÍDA,
  // senão some no mesmo frame em que `visible` vira false e a animação de fechar
  // não chega a aparecer.
  const [editModalMounted, setEditModalMounted] = useState(false);

  useEffect(() => {
    if (isEditModalVisible) {
      setEditModalMounted(true);
      Animated.parallel([
        Animated.spring(editSlideAnim, { toValue: 0, tension: 50, friction: 10, useNativeDriver: true }),
        Animated.timing(editFadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(editSlideAnim, { toValue: windowHeight, duration: 250, useNativeDriver: true }),
        Animated.timing(editFadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
      ]).start(({ finished }) => {
        if (finished) setEditModalMounted(false);
      });
    }
  }, [isEditModalVisible, editSlideAnim, editFadeAnim, windowHeight]);
  const [editForm, setEditForm] = useState({ name: '', username: '', avatarSeed: 'happy', fotoUrl: null, bio: '' });
  const [isSaving, setIsSaving] = useState(false);
  // Feedback estilizado (substitui Alert.alert nativo no fluxo da foto).
  const [feedback, setFeedback] = useState(null); // { variant, title, message } | null
  // Foto já enviada ao bucket NESTA sessão do modal e ainda NÃO salva no perfil.
  // Enquanto estiver aqui, o arquivo não é referenciado por ninguém — se o
  // usuário fechar/remover/trocar, vira órfão e precisa ser apagado.
  const pendingPhotoRef = useRef(null);

  // Exclusão de conta (modal de confirmação com senha)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();
  const userId = user?.id;
  const queriesEnabled = isAuthenticated && !!userId;

  // Fonte ÚNICA da tela (React Query, persistido): 1 RPC agregado = 1 round-trip
  // (com fallback no service para o caminho antigo se a migração ainda não foi
  // aplicada). Substitui os 5 hooks granulares anteriores.
  const profileScreenQuery = useProfileScreen(userId, queriesEnabled);

  // Estado de tela derivado da query principal (não de transições de status):
  // correto mesmo em error→error (retry que falha de novo não trava o spinner).
  const profileScreenState = useScreenQueryState(profileScreenQuery, { enabled: queriesEnabled });

  // Recarrega ao voltar o foco à tela. Sem isto, responder questões em outra
  // tela (que incrementam `questoes_respondidas` no banco) NÃO refletia aqui:
  // este cache é persistido (staleTime 5 min) e nada o invalidava → o perfil
  // mostrava contagem velha. Guard `hasLoadedOnce` pula o 1º foco (o mount já
  // busca). `refetch()` é de fundo (liga isFetching, não isLoading) → sem skeleton.
  const hasLoadedOnce = useRef(false);
  // `refetch` do React Query v5 tem identidade estável — destructuring satisfaz
  // o exhaustive-deps sem re-assinar o listener a cada render.
  const refetchProfileScreen = profileScreenQuery.refetch;
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (hasLoadedOnce.current && queriesEnabled) {
        refetchProfileScreen();
      }
      hasLoadedOnce.current = true;
    });
    return unsubscribe;
  }, [navigation, queriesEnabled, refetchProfileScreen]);

  // Tonalidade do skeleton conforme tema (sobrescreve a cor roxa padrão do
  // SkeletonLoading via prop `style`, que é aplicada após `skeletonBase`).
  const skeletonTint = useMemo(
    () => ({ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.13)' : 'rgba(0,0,0,0.09)' }),
    [isDarkMode]
  );

  // Loading por seção: como agora há UMA query agregada, todas as flags derivam
  // dela (`isLoading` = sem dado e buscando; false quando desabilitada, com dado,
  // ou em refetch de fundo). Nunca decidir skeleton pelo valor do estado (distingue
  // "carregando" de "0 real"). Mantidas nomeadas para o render de skeleton ficar legível.
  const screenLoading = profileScreenQuery.isLoading;
  const statsLoading = screenLoading;
  const weeklyLoading = screenLoading;
  const goalLoading = screenLoading;
  const achLoading = screenLoading;
  const actLoading = screenLoading;
  // Identidade já vem pré-preenchida do AuthContext no boot; skeleton só se não
  // houver perfil ainda (cold start sem cache).
  const identityLoading = screenLoading && !profile?.nome;

  // Sincroniza o payload agregado no estado da tela (render inalterado).
  useEffect(() => {
    const value = profileScreenQuery.data;
    if (!value) return;
    if (value.userData) {
      setUserData(prev => {
        const newData = value.userData;
        // Mantem referencia da foto se inalterada (evita flash), como antes
        if (prev.fotoUrl === newData.fotoUrl && prev.avatarSeed === newData.avatarSeed) {
          return { ...prev, ...newData, fotoUrl: prev.fotoUrl, avatarSeed: prev.avatarSeed };
        }
        return newData;
      });
    }
    if (value.stats) setStats(value.stats);
    if (value.achievements?.length > 0) setAchievements(value.achievements);
    if (value.activities?.length > 0) setActivities(value.activities);
    if (value.goal) setNextGoal(value.goal);
    if (value.weekly) setWeeklyStats(value.weekly);
  }, [profileScreenQuery.data]);

  // Pull-to-refresh: refetch da query única
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await profileScreenQuery.refetch();
    } finally {
      setRefreshing(false);
    }
  }, [profileScreenQuery]);

  // Handler de navegação memoizado
  // `goBack()` puro não faz NADA quando não há tela anterior na pilha — e na web
  // isso é o caso comum: a URL é compartilhável e recarregável, então abrir
  // /Profile direto (ou dar F5 nele) deixa a pilha com uma entrada só. O botão
  // parecia quebrado sem estar. No app quase nunca acontece, porque se chega ao
  // Perfil sempre navegando.
  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('Home');
  }, [navigation]);
  const handleNavigateToPerformance = useCallback(() => navigation.navigate('Performance'), [navigation]);
  const handleNavigateToBenchmark = useCallback(() => navigation.navigate('CompetitorBenchmark'), [navigation]);

  // Handler de mudança de tab memoizado
  const handleTabChange = useCallback((tab) => setActiveTab(tab), []);

  const weeklyAccuracyProgress = useMemo(
    () => Math.min(100, Math.max(0, Number(weeklyStats?.percentage) || 0)),
    [weeklyStats?.percentage]
  );
  const weeklyCorrectProgress = weeklyAccuracyProgress;
  const weeklyWrongProgress = useMemo(() => {
    const total = Number(weeklyStats?.total) || 0;
    if (total <= 0) return 0;
    return Math.min(100, Math.round(((Number(weeklyStats?.wrong) || 0) / total) * 100));
  }, [weeklyStats?.total, weeklyStats?.wrong]);
  const weeklyTotalProgress = useMemo(
    () => ((Number(weeklyStats?.total) || 0) > 0 ? 100 : 0),
    [weeklyStats?.total]
  );
  const weeklyRingOffset = useMemo(
    () => PROFILE_WEEKLY_RING_CIRCUMFERENCE - (weeklyAccuracyProgress / 100) * PROFILE_WEEKLY_RING_CIRCUMFERENCE,
    [weeklyAccuracyProgress]
  );
  // Paleta derivada da cor-base #03386C (hue 210, sat 95%), mesma do cover.
  // Ordem clara -> escura (como era no cinza): a cor-base fica no canto superior
  // esquerdo, onde estao o titulo e o subtitulo. Textos seguem brancos.
  const weeklyCardColors = useMemo(() => ['#03386C', '#022445', '#01172D'], []);
  const weeklyTextColor = useMemo(() => '#FFFFFF', []);
  const weeklyTextSecondary = useMemo(() => 'rgba(255, 255, 255, 0.8)', []);
  const weeklyTrackColor = useMemo(() => 'rgba(255,255,255,0.22)', []);
  const weeklyBarBg = useMemo(() => 'rgba(255,255,255,0.15)', []);
  const weeklyAccentColor = useMemo(() => '#FFFFFF', []);
  // Preenchimento das 3 barrinhas (Respostas/Acertos/Erros). Amarelo #E9C71F —
  // o mesmo ja usado no tile "Jogos" da Home. 7:1 sobre o azul do card.
  // O anel de % segue em weeklyAccentColor (branco).
  const weeklyBarFill = useMemo(() => '#E9C71F', []);

  // Handler de logout
  const handleLogout = useCallback(() => {
    Alert.alert(
      'Sair da conta',
      'Tem certeza que deseja sair?',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Sair',
          style: 'destructive',
          onPress: async () => {
            await signOut();
            // Deslogado entra pela tela de boas-vindas (Home é gated desde
            // 2026-08-05). Resetar para 'Home' aqui deixaria o usuário numa
            // rota que o guard imediatamente rejeitaria.
            navigation.reset({
              index: 0,
              routes: [{ name: 'Onboarding' }],
            });
          },
        },
      ]
    );
  }, [navigation]);

  // Handler para abrir modal de edição
  const handleOpenEditModal = useCallback(() => {
    pendingPhotoRef.current = null; // sessão nova: nada pendente ainda
    setEditForm({
      name: userData.name,
      username: userData.username,
      avatarSeed: userData.avatarSeed || 'happy',
      fotoUrl: userData.fotoUrl || null,
      bio: userData.bio,
    });
    setIsEditModalVisible(true);
  }, [userData]);

  // Fechar sem salvar: o upload desta sessão nunca chegou ao perfil → apaga o
  // arquivo (best-effort) para não acumular órfão no bucket.
  const handleCloseEditModal = useCallback(() => {
    if (pendingPhotoRef.current) {
      deleteProfilePhoto(pendingPhotoRef.current);
      pendingPhotoRef.current = null;
    }
    setIsEditModalVisible(false);
  }, []);

  // Handler para escolher foto da galeria
  const handlePickPhoto = useCallback(async () => {
    const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (!permissionResult.granted) {
      setFeedback({ variant: 'error', title: 'Permissão necessária', message: 'Precisamos de permissão para acessar sua galeria.' });
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      // O cropper nativo do Android é instável (etapa de cortar sem botão de
      // confirmar). Pegamos a imagem inteira e recortamos em quadrado via
      // ImageManipulator, garantindo que o fluxo sempre conclua.
      allowsEditing: false,
      quality: 1,
    });

    if (result.canceled || !result.assets?.[0]) return;

    setIsSaving(true);
    try {
      const asset = result.assets[0];

      // Recorte central para quadrado + redimensionamento (avatar)
      let photoUri = asset.uri;
      if (asset.width && asset.height) {
        const size = Math.min(asset.width, asset.height);
        const originX = Math.floor((asset.width - size) / 2);
        const originY = Math.floor((asset.height - size) / 2);
        const processed = await ImageManipulator.manipulateAsync(
          asset.uri,
          [
            { crop: { originX, originY, width: size, height: size } },
            { resize: { width: 512, height: 512 } },
          ],
          { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
        );
        photoUri = processed.uri;
      }

      const uploadResult = await uploadProfilePhoto(photoUri);
      if (uploadResult.success) {
        // Trocou de foto antes de salvar: o upload anterior desta sessão já
        // não serve pra nada → apaga antes de assumir o novo como pendente.
        if (pendingPhotoRef.current && pendingPhotoRef.current !== uploadResult.url) {
          deleteProfilePhoto(pendingPhotoRef.current);
        }
        pendingPhotoRef.current = uploadResult.url;
        setEditForm(prev => ({ ...prev, fotoUrl: uploadResult.url, avatarSeed: null }));
        setFeedback({ variant: 'success', title: 'Foto carregada', message: 'Clique em Salvar para confirmar a alteração.' });
      } else {
        setFeedback({ variant: 'error', title: 'Erro', message: uploadResult.error || 'Não foi possível carregar a foto.' });
      }
    } catch {
      setFeedback({ variant: 'error', title: 'Erro', message: 'Não foi possível processar a foto.' });
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Handler para remover foto e usar avatar
  const handleRemovePhoto = useCallback(() => {
    // Só apaga aqui o que foi enviado nesta sessão e nunca salvo. A foto já
    // salva no perfil continua no bucket até o Salvar confirmar a remoção
    // (senão um "Remover" seguido de Cancelar deixaria o perfil apontando
    // para um arquivo inexistente).
    if (pendingPhotoRef.current) {
      deleteProfilePhoto(pendingPhotoRef.current);
      pendingPhotoRef.current = null;
    }
    setEditForm(prev => ({ ...prev, fotoUrl: null, avatarSeed: prev.avatarSeed || 'happy' }));
  }, []);

  // Estável para que AvatarGridItem (memo) não re-renderize a cada tecla digitada.
  const handleSelectAvatar = useCallback((seed) => {
    setEditForm(prev => ({ ...prev, avatarSeed: seed }));
  }, []);

  // Handler para salvar alterações no Supabase
  const handleSaveProfile = useCallback(async () => {
    // Validação antes do round-trip: sem isto dava pra salvar nome vazio e
    // username vazio (o perfil virava só "@"). O input já restringe os
    // caracteres a [a-zA-Z0-9_]; aqui só o que ele não cobre.
    const nome = (editForm.name || '').trim();
    const username = (editForm.username || '').trim();
    if (!nome) {
      setFeedback({ variant: 'error', title: 'Nome obrigatório', message: 'Digite seu nome para continuar.' });
      return;
    }
    if (username.length < 3) {
      setFeedback({ variant: 'error', title: 'Username inválido', message: 'O username precisa ter pelo menos 3 caracteres.' });
      return;
    }

    // Foto que está salva HOJE: se o salvamento trocar/remover a foto, este
    // arquivo deixa de ser referenciado e precisa sair do bucket.
    const previousPhotoUrl = userData.fotoUrl || null;

    setIsSaving(true);
    const result = await updateUserProfile({
      nome,
      username,
      avatar_seed: editForm.fotoUrl ? null : editForm.avatarSeed,
      foto_url: editForm.fotoUrl,
      bio: editForm.bio,
    });

    if (result.success) {
      setUserData(prev => ({
        ...prev,
        name: nome,
        username,
        avatarSeed: editForm.fotoUrl ? null : editForm.avatarSeed,
        fotoUrl: editForm.fotoUrl,
        bio: editForm.bio,
      }));
      // O upload desta sessão virou a foto oficial → não é mais órfão.
      pendingPhotoRef.current = null;
      // Só depois do sucesso: a foto antiga ficou sem referência (troca ou
      // remoção). Best-effort — falhar aqui não afeta o perfil já salvo.
      if (previousPhotoUrl && previousPhotoUrl !== editForm.fotoUrl) {
        deleteProfilePhoto(previousPhotoUrl);
      }
      setIsEditModalVisible(false);
      // Atualiza o AuthContext para refletir em outras telas (ex: HomeScreen)
      await refreshProfile();
      queryClient.invalidateQueries({ queryKey: profileKeys.all });
      setFeedback({ variant: 'success', title: 'Sucesso', message: 'Perfil atualizado com sucesso!' });
    } else {
      setFeedback({ variant: 'error', title: 'Erro', message: result.error || 'Não foi possível salvar as alterações.' });
    }
    setIsSaving(false);
  }, [editForm, refreshProfile, queryClient, userData.fotoUrl]);

  // Handler de exclusão de conta: revalida a senha, apaga a conta + dados
  // (via Edge Function) e leva o usuário de volta ao fluxo inicial.
  const handleConfirmDelete = useCallback(async () => {
    if (!deletePassword) {
      Alert.alert('Senha obrigatória', 'Digite sua senha para confirmar a exclusão.');
      return;
    }
    setIsDeleting(true);
    const result = await deleteAccount(deletePassword);
    setIsDeleting(false);

    if (result.success) {
      setIsDeleteModalVisible(false);
      setDeletePassword('');
      Alert.alert('Conta excluída', 'Sua conta e seus dados foram removidos.');
      // Mesma razão do logout: sem sessão, a porta de entrada é o Onboarding.
      navigation.reset({ index: 0, routes: [{ name: 'Onboarding' }] });
    } else {
      Alert.alert('Não foi possível excluir', result.error || 'Tente novamente.');
    }
  }, [deletePassword, navigation]);

  // Sem gate de loading de tela cheia: o shell (header, cards, abas, títulos)
  // renderiza na hora e cada seção mostra skeleton granular até sua query
  // resolver — ver flags *Loading + skeletonTint acima.

  // Erro: query falhou (rede/timeout/RLS), sem dado e fora de refetch.
  // O retry chama refetch() direto — isFetching liga → profileScreenState.loading
  // cobre o spinner da tentativa. Falha repetida (error→error) NÃO trava, pois o
  // estado é derivado, não espelhado em useState.
  if (profileScreenState.error) {
    const err = profileScreenState.error;
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center', padding: 24, backgroundColor: dynamicColors.bgPrimary }]}>
        <Ionicons name="cloud-offline-outline" size={48} color={dynamicColors.textTertiary} />
        <Text style={{ color: dynamicColors.textPrimary, fontSize: 16, fontWeight: '700', marginTop: 16, textAlign: 'center' }}>
          Não foi possível carregar seu perfil
        </Text>
        <Text style={{ color: dynamicColors.textSecondary, fontSize: 13, marginTop: 8, textAlign: 'center' }}>
          Verifique sua conexão e tente novamente.
        </Text>
        {__DEV__ && (
          <Text style={{ color: dynamicColors.textTertiary, fontSize: 11, marginTop: 8, textAlign: 'center' }}>
            [{err?.name || 'Error'}] {err?.code ? `${err.code}: ` : ''}{err?.message}
          </Text>
        )}
        <TouchableOpacity
          style={{ marginTop: 20, backgroundColor: dynamicColors.accentPrimary, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 12 }}
          onPress={profileScreenState.retry}
        >
          <Text style={{ color: '#FFFFFF', fontWeight: '600' }}>Tentar novamente</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Seções da tela como FUNÇÕES DE RENDER (chamadas `{ProfileHeader()}`, nunca
  // `<ProfileHeader />`): declaradas dentro do componente, cada render criaria
  // uma função NOVA → como elemento JSX o React veria um "tipo" diferente e
  // DESMONTARIA/REMONTARIA a subárvore inteira (era a causa-raiz do piscar de
  // avatar/imagens e do scroll de conquistas resetando a cada tecla no modal).
  // Como chamada de função, o JSX funde na árvore da própria ProfileScreen e é
  // reconciliado normalmente. Nenhuma delas usa hooks (só closures) — manter assim.

  // Header do Perfil
  const ProfileHeader = () => (
    <LinearGradient
      colors={COVER_GRADIENT}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.profileHeader}
    >
      {/* Faixa do topo (ícones) — herda o gradiente do bloco inteiro */}
      <View style={styles.coverGradient}>
        {/* Padrão decorativo */}
        <View style={styles.coverPattern} />

        {/* Botão voltar */}
        <TouchableOpacity
          style={styles.backBtn}
          onPress={handleGoBack}
        >
          <Ionicons name="arrow-back" size={20} color="#FFFFFF" />
        </TouchableOpacity>

        {/* Botão usuários bloqueados */}
        <TouchableOpacity style={styles.blockedBtn} onPress={() => navigation.navigate('BlockedUsers')}>
          <Ionicons name="ban-outline" size={20} color="#FFFFFF" />
        </TouchableOpacity>

        {/* Botão sair/logout */}
        <TouchableOpacity style={styles.settingsBtn} onPress={handleLogout}>
          <Ionicons name="log-out-outline" size={22} color="#FFFFFF" />
        </TouchableOpacity>

        {/* Botão de alternar tema */}
        <TouchableOpacity style={styles.themeToggleBtn} onPress={toggleTheme}>
          <Ionicons name={isDarkMode ? 'sunny-outline' : 'moon-outline'} size={18} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Conteúdo do perfil */}
      {/* `box-none`: este bloco tem marginTop:-50 (avatar sobe por cima da capa)
          e é irmão POSTERIOR da faixa de ícones — sem isto ele fica por cima dos
          4 botões do cabeçalho e, como todo View tem pointerEvents "auto", captura
          o toque deles (ficavam visíveis mas não respondiam). Com box-none o
          container deixa de ser alvo, seus filhos tocáveis continuam funcionando
          e o toque em área vazia atravessa até os botões atrás. */}
      {/* `pointerEvents` vai como PROP, não dentro do style.
          MEDIDO em 2026-08-30 (sonda `elementFromPoint` no aparelho): com
          `style={{ pointerEvents: 'box-none' }}` este container aparecia com
          `pointer-events: auto` e cobria os 4 botões do cabeçalho — eles ficavam
          visíveis e MORTOS ao toque (bug I9b/I9c, de volta pela porta dos fundos).
          O react-native-web só traduz `box-none` para estilos REGISTRADOS
          (`StyleSheet.create`), onde gera `pointer-events:none` + `auto` nos
          filhos diretos. Inline vira CSS inválido e é DESCARTADO EM SILÊNCIO.
          O resto do app já usa a prop; só esta tela destoava. */}
      <View pointerEvents="box-none" style={styles.profileInfoContainer}>
        {/* Avatar */}
        {/* Mesmo motivo: o avatar centralizado invade a faixa do botão de
            bloqueados em telas estreitas (~26 dos 36px num aparelho de 320dp).
            O wrapper não é alvo; a foto em si não é tocável ("none"); só o
            avatarEditBtn permanece clicável. */}
        <View pointerEvents="box-none" style={styles.avatarWrapper}>
          <View pointerEvents="none" style={styles.avatarContainer}>
            <ProfileAvatar
              fotoUrl={userData.fotoUrl}
              avatarSeed={userData.avatarSeed}
              style={styles.avatarImage}
            />
          </View>
          <View style={[styles.statusIndicator, styles.statusOnline]} />
          <TouchableOpacity style={styles.avatarEditBtn} onPress={handleOpenEditModal}>
            <Ionicons name="pencil" size={14} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {/* Nome e badges */}
        <View style={styles.nameSection}>
          {identityLoading ? (
            <SkeletonText width={150} height={22} style={skeletonTint} />
          ) : (
            <Text style={styles.userName}>{userData.name}</Text>
          )}
          {!identityLoading && userData.isVerified && (
            <View style={styles.verifiedBadge}>
              <Ionicons name="checkmark" size={13} color="#FFFFFF" />
            </View>
          )}
          {userData.isPremium && (
            <LinearGradient
              colors={['#FFD700', '#FFA500']}
              style={styles.premiumBadge}
            >
              <Ionicons name="diamond" size={12} color="#FFFFFF" />
              <Text style={styles.premiumText}>PRO</Text>
            </LinearGradient>
          )}
        </View>

        {/* Username e membro desde */}
        <View style={styles.metaRow}>
          {identityLoading ? (
            <SkeletonText width={180} height={13} style={skeletonTint} />
          ) : (
            <>
              <Text style={styles.username}>@{userData.username}</Text>
              {!!userData.memberSince && (
                <>
                  <Text style={styles.metaSeparator}>•</Text>
                  <Ionicons name="calendar-outline" size={13} color={colors.textSecondary} style={{ marginRight: 4 }} />
                  <Text style={styles.memberSince}>Membro desde {userData.memberSince}</Text>
                </>
              )}
            </>
          )}
        </View>

        {/* Bio */}
        {identityLoading ? (
          <View style={{ alignItems: 'center', marginVertical: 10 }}>
            <SkeletonText width={240} height={12} style={skeletonTint} />
            <SkeletonText width={190} height={12} style={[skeletonTint, { marginTop: 6 }]} />
          </View>
        ) : (
          <Text style={styles.bio}>{userData.bio}</Text>
        )}

        {/* Streak Counter */}
        <View style={styles.streakContainer}>
          {/* Amarelo do card "Minha Posicao" — igual ao STREAK_YELLOW dos estilos. */}
          <Ionicons name="flame" size={20} color="#FCF0B6" />
          {statsLoading ? (
            <SkeletonText width={26} height={20} style={[skeletonTint, { marginHorizontal: 6 }]} />
          ) : (
            <Text style={styles.streakNumber}>{userData.streak.current}</Text>
          )}
          <Text style={styles.streakLabel}>dias de sequência</Text>
          <View style={styles.streakProgressBar}>
            <View style={[styles.streakProgressFill, { width: `${userData.streak.progress}%` }]} />
          </View>
        </View>

        {/* Quick Stats */}
        <View style={styles.quickStats}>
          <View style={[styles.quickStat, { borderColor: colors.border, backgroundColor: colors.bgCard }]}>
            {statsLoading ? (
              <SkeletonText width={48} height={18} style={skeletonTint} />
            ) : (
              <Text style={[styles.quickStatValue, { color: colors.textPrimary }]}>{stats.questionsAnswered.toLocaleString()}</Text>
            )}
            <Text style={[styles.quickStatLabel, { color: colors.textTertiary }]}>Questões</Text>
          </View>
          <View style={[styles.quickStat, { borderColor: colors.border, backgroundColor: colors.bgCard }]}>
            {statsLoading ? (
              <SkeletonText width={30} height={18} style={skeletonTint} />
            ) : (
              <Text style={[styles.quickStatValue, { color: colors.textPrimary }]}>{stats.achievements}</Text>
            )}
            <Text style={[styles.quickStatLabel, { color: colors.textTertiary }]}>Conquistas</Text>
          </View>
          <View style={[styles.quickStat, { borderColor: colors.border, backgroundColor: colors.bgCard }]}>
            {statsLoading ? (
              <SkeletonText width={30} height={18} style={skeletonTint} />
            ) : (
              <Text style={[styles.quickStatValue, { color: colors.textPrimary }]}>{stats.followers}</Text>
            )}
            <Text style={[styles.quickStatLabel, { color: colors.textTertiary }]}>Seguidores</Text>
          </View>
          <View style={[styles.quickStat, { borderColor: colors.border, backgroundColor: colors.bgCard }]}>
            {statsLoading ? (
              <SkeletonText width={34} height={18} style={skeletonTint} />
            ) : (
              <Text style={[styles.quickStatValue, { color: colors.accentPrimary }]}>#{stats.globalRank}</Text>
            )}
            <Text style={[styles.quickStatLabel, { color: colors.textTertiary }]}>Ranking</Text>
          </View>
        </View>

        {/* Botões de ação (linha única): Visão Geral · Posts · Editar Perfil · Compartilhar */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.actionBtn, { borderColor: colors.border }, activeTab === 'overview' && { backgroundColor: colors.accentPrimary, borderColor: colors.accentPrimary }]}
            onPress={() => handleTabChange('overview')}
            accessibilityRole="button"
            accessibilityState={{ selected: activeTab === 'overview' }}
          >
            <Text numberOfLines={1} adjustsFontSizeToFit style={[styles.actionBtnText, { color: activeTab === 'overview' ? '#FFFFFF' : colors.textSecondary }]}>
              Visão Geral
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionBtn, { borderColor: colors.border }, activeTab === 'posts' && { backgroundColor: colors.accentPrimary, borderColor: colors.accentPrimary }]}
            onPress={() => handleTabChange('posts')}
            accessibilityRole="button"
            accessibilityState={{ selected: activeTab === 'posts' }}
          >
            <Text numberOfLines={1} adjustsFontSizeToFit style={[styles.actionBtnText, { color: activeTab === 'posts' ? '#FFFFFF' : colors.textSecondary }]}>
              Posts
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionBtn, { borderColor: colors.border }]}
            onPress={handleOpenEditModal}
          >
            <Text numberOfLines={1} adjustsFontSizeToFit style={[styles.actionBtnText, { color: colors.textPrimary }]}>
              Editar Perfil
            </Text>
          </TouchableOpacity>

          <TouchableOpacity style={[styles.actionBtn, { borderColor: colors.border }]}>
            <Text numberOfLines={1} adjustsFontSizeToFit style={[styles.actionBtnText, { color: colors.textPrimary }]}>
              Compartilhar
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </LinearGradient>
  );

  // Card de Desempenho Semanal
  const WeeklyPerformanceCard = () => (
    <TouchableOpacity
      style={styles.weeklyCard}
      onPress={handleNavigateToPerformance}
      activeOpacity={0.86}
      accessibilityRole="button"
      accessibilityLabel={`Desempenho semanal. ${weeklyStats.percentage}% de acerto, ${weeklyStats.total} respostas, ${weeklyStats.correct} acertos e ${weeklyStats.wrong} erros.`}
    >
      <LinearGradient
        colors={weeklyCardColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.weeklyGradient}
      >
        <View style={styles.weeklyBody}>
          <View style={styles.weeklyLeft}>
            <View style={styles.weeklyTitles}>
              <Text style={[styles.weeklyTitle, { color: weeklyTextColor }]}>Desempenho Semanal</Text>
              <Text style={[styles.weeklySubtitle, { color: weeklyTextSecondary }]}>Últimos 7 dias</Text>
            </View>

            <View style={styles.weeklyActivityStats}>
              <View style={styles.weeklyActivityStat}>
                <View style={styles.weeklyStatValueRow}>
                  {weeklyLoading ? (
                    <SkeletonText width={26} height={18} style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
                  ) : (
                    <Text style={[styles.weeklyStatValue, { color: weeklyTextColor }]}>{weeklyStats.total}</Text>
                  )}
                </View>
                <Text style={[styles.weeklyStatLabel, { color: weeklyTextSecondary }]}>Respostas</Text>
                <View style={[styles.weeklyStatBar, { backgroundColor: weeklyBarBg }]}>
                  <View style={[styles.weeklyStatBarFill, { width: `${weeklyTotalProgress}%`, backgroundColor: weeklyBarFill }]} />
                </View>
              </View>

              <View style={styles.weeklyActivityStat}>
                <View style={styles.weeklyStatValueRow}>
                  {weeklyLoading ? (
                    <SkeletonText width={26} height={18} style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
                  ) : (
                    <Text style={[styles.weeklyStatValue, { color: weeklyTextColor }]}>{weeklyStats.correct}</Text>
                  )}
                </View>
                <Text style={[styles.weeklyStatLabel, { color: weeklyTextSecondary }]}>Acertos</Text>
                <View style={[styles.weeklyStatBar, { backgroundColor: weeklyBarBg }]}>
                  <View style={[styles.weeklyStatBarFill, { width: `${weeklyCorrectProgress}%`, backgroundColor: weeklyBarFill }]} />
                </View>
              </View>

              <View style={styles.weeklyActivityStat}>
                <View style={styles.weeklyStatValueRow}>
                  {weeklyLoading ? (
                    <SkeletonText width={26} height={18} style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
                  ) : (
                    <Text style={[styles.weeklyStatValue, { color: weeklyTextColor }]}>{weeklyStats.wrong}</Text>
                  )}
                </View>
                <Text style={[styles.weeklyStatLabel, { color: weeklyTextSecondary }]}>Erros</Text>
                <View style={[styles.weeklyStatBar, { backgroundColor: weeklyBarBg }]}>
                  <View style={[styles.weeklyStatBarFill, { width: `${weeklyWrongProgress}%`, backgroundColor: weeklyBarFill }]} />
                </View>
              </View>
            </View>
          </View>

          <View style={styles.weeklyRing}>
            <Svg width={PROFILE_WEEKLY_RING_SIZE} height={PROFILE_WEEKLY_RING_SIZE} viewBox={`0 0 ${PROFILE_WEEKLY_RING_SIZE} ${PROFILE_WEEKLY_RING_SIZE}`}>
              <Circle
                cx={PROFILE_WEEKLY_RING_SIZE / 2}
                cy={PROFILE_WEEKLY_RING_SIZE / 2}
                r={PROFILE_WEEKLY_RING_RADIUS}
                stroke={weeklyTrackColor}
                strokeWidth={PROFILE_WEEKLY_RING_STROKE}
                fill="none"
              />
              <Circle
                cx={PROFILE_WEEKLY_RING_SIZE / 2}
                cy={PROFILE_WEEKLY_RING_SIZE / 2}
                r={PROFILE_WEEKLY_RING_RADIUS}
                stroke={weeklyAccentColor}
                strokeWidth={PROFILE_WEEKLY_RING_STROKE}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${PROFILE_WEEKLY_RING_CIRCUMFERENCE} ${PROFILE_WEEKLY_RING_CIRCUMFERENCE}`}
                strokeDashoffset={weeklyRingOffset}
                transform={`rotate(-90 ${PROFILE_WEEKLY_RING_SIZE / 2} ${PROFILE_WEEKLY_RING_SIZE / 2})`}
              />
            </Svg>
            <View style={styles.weeklyRingInner}>
              {weeklyLoading ? (
                <SkeletonText width={38} height={18} style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
              ) : (
                <Text style={[styles.weeklyRingValue, { color: weeklyTextColor }]}>{weeklyStats.percentage}%</Text>
              )}
              <Text style={[styles.weeklyRingLabel, { color: weeklyTextSecondary }]}>Acerto</Text>
            </View>
          </View>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );

  // Cards de Estatísticas
  const StatsCards = () => {
    return (
      <View style={styles.statsSection}>
        <Text style={[styles.sectionTitle, { color: dynamicColors.textPrimary }]}>
          <Ionicons name="stats-chart" size={18} color={dynamicColors.textPrimary} />{' '}Estatísticas
        </Text>
        <View style={styles.statsGrid}>
          {/* Questões Respondidas */}
          <View style={[styles.statCard, styles.glassCard, { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border }]}>
            <View style={[styles.statCardIcon, { backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.12)' }]}>
              <Ionicons name="document-text" size={16} color="#FF9800" />
            </View>
            <View style={styles.statCardContent}>
              {statsLoading ? (
                <SkeletonText width={72} height={20} style={skeletonTint} />
              ) : (
                <Text style={[styles.statCardValue, { color: dynamicColors.textPrimary }]}>{stats.questionsAnswered.toLocaleString()}</Text>
              )}
              <Text style={[styles.statCardLabel, { color: dynamicColors.textSecondary }]}>Questões Respondidas</Text>
              <View style={styles.trendPositive}>
                <Ionicons name="arrow-up" size={10} color="#51CF66" />
                <Text style={styles.trendText}> +12% esta semana</Text>
              </View>
            </View>
          </View>

          {/* Taxa de Acerto */}
          <View style={[styles.statCard, styles.glassCard, { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border }]}>
            <View style={[styles.statCardIcon, { backgroundColor: isDarkMode ? 'rgba(81, 207, 102, 0.15)' : 'rgba(81, 207, 102, 0.12)' }]}>
              <Ionicons name="checkmark-circle" size={16} color="#51CF66" />
            </View>
            <View style={styles.statCardContent}>
              {statsLoading ? (
                <SkeletonText width={56} height={20} style={skeletonTint} />
              ) : (
                <Text style={[styles.statCardValue, { color: dynamicColors.textPrimary }]}>{stats.accuracyRate}%</Text>
              )}
              <Text style={[styles.statCardLabel, { color: dynamicColors.textSecondary }]}>Taxa de Acerto</Text>
              <View style={styles.trendPositive}>
                <Ionicons name="arrow-up" size={10} color="#51CF66" />
                <Text style={styles.trendText}> +5% vs. mês passado</Text>
              </View>
            </View>
          </View>

          {/* Tempo de Estudo */}
          <View style={[styles.statCard, styles.glassCard, { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border }]}>
            <View style={[styles.statCardIcon, { backgroundColor: isDarkMode ? 'rgba(91, 143, 249, 0.15)' : 'rgba(91, 143, 249, 0.12)' }]}>
              <Ionicons name="time" size={16} color="#5B8FF9" />
            </View>
            <View style={styles.statCardContent}>
              {statsLoading ? (
                <SkeletonText width={64} height={20} style={skeletonTint} />
              ) : (
                <Text style={[styles.statCardValue, { color: dynamicColors.textPrimary }]}>{stats.totalStudyTime}</Text>
              )}
              <Text style={[styles.statCardLabel, { color: dynamicColors.textSecondary }]}>Tempo de Estudo</Text>
              <Text style={[styles.trendNeutral, { color: dynamicColors.textTertiary }]}>Média: 2h/dia</Text>
            </View>
          </View>

          {/* Ranking Global */}
          <View style={[styles.statCard, styles.glassCard, { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border }]}>
            <View style={[styles.statCardIcon, { backgroundColor: isDarkMode ? 'rgba(255, 159, 67, 0.15)' : 'rgba(255, 159, 67, 0.12)' }]}>
              <Ionicons name="trophy" size={16} color="#FF9F43" />
            </View>
            <View style={styles.statCardContent}>
              {statsLoading ? (
                <SkeletonText width={48} height={20} style={skeletonTint} />
              ) : (
                <Text style={[styles.statCardValue, { color: dynamicColors.textPrimary }]}>#{stats.globalRank}</Text>
              )}
              <Text style={[styles.statCardLabel, { color: dynamicColors.textSecondary }]}>Ranking Global</Text>
              <View style={styles.trendPositive}>
                <Ionicons name="arrow-up" size={10} color="#51CF66" />
                <Text style={styles.trendText}> Subiu {stats.rankChange} posições</Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  };

  // Próximo Objetivo
  const NextGoalCard = () => {
    // Guarda contra target 0/ausente (NaN%) e clamp 0–100 (current > target)
    const goalTarget = Number(nextGoal.target) || 0;
    const goalPercent = goalTarget > 0
      ? Math.min(100, Math.max(0, ((Number(nextGoal.current) || 0) / goalTarget) * 100))
      : 0;
    return (
    <View style={styles.goalSection}>
      <Text style={[styles.sectionTitle, { color: dynamicColors.textPrimary }]}>🎯 Próximo Objetivo</Text>
      <View style={[styles.goalCard, { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border }]}>
        <Text style={styles.goalIcon}>{goalLoading ? '🎯' : nextGoal.icon}</Text>
        <View style={styles.goalInfo}>
          {goalLoading ? (
            <>
              <SkeletonText width={120} height={15} style={skeletonTint} />
              <SkeletonText width={170} height={12} style={[skeletonTint, { marginTop: 6 }]} />
            </>
          ) : (
            <>
              <Text style={[styles.goalName, { color: dynamicColors.textPrimary }]}>{nextGoal.name}</Text>
              <Text style={[styles.goalDesc, { color: dynamicColors.textSecondary }]}>{nextGoal.desc}</Text>
            </>
          )}
        </View>
        <View style={styles.goalProgress}>
          {goalLoading ? (
            <SkeletonText width={36} height={14} style={skeletonTint} />
          ) : (
            <Text style={[styles.goalProgressText, { color: dynamicColors.textPrimary }]}>{nextGoal.current}/{nextGoal.target}</Text>
          )}
          <View style={[styles.goalProgressBar, { backgroundColor: dynamicColors.bgTertiary }]}>
            <LinearGradient
              colors={[dynamicColors.accentPrimary, colors.accentPurple]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[styles.goalProgressFill, { width: `${goalPercent}%` }]}
            />
          </View>
        </View>
      </View>
    </View>
    );
  };

  // Grid de Conquistas
  const AchievementsGrid = () => (
    <View style={styles.achievementsSection}>
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: dynamicColors.textPrimary }]}>🏆 Conquistas</Text>
        <TouchableOpacity>
          <Text style={[styles.viewAllLink, { color: dynamicColors.accentPrimary }]}>Ver todas{statsLoading ? '' : ` (${stats.achievements})`}</Text>
        </TouchableOpacity>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.achievementsScroll}>
        {achLoading ? (
          [0, 1, 2, 3].map((i) => (
            <View
              key={`ach-sk-${i}`}
              style={[styles.achievementCard, { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border }]}
            >
              <SkeletonCircle size={44} style={[skeletonTint, { marginBottom: 10 }]} />
              <SkeletonText width={80} height={12} style={skeletonTint} />
              <SkeletonText width={100} height={10} style={[skeletonTint, { marginTop: 6 }]} />
            </View>
          ))
        ) : (
          achievements.map((achievement) => (
          <View
            key={achievement.id}
            style={[
              styles.achievementCard,
              { backgroundColor: dynamicColors.bgCard, borderColor: dynamicColors.border },
              achievement.unlocked ? styles.achievementUnlocked : styles.achievementLocked,
              achievement.rarity === 'rare' && styles.achievementRare,
              achievement.rarity === 'epic' && styles.achievementEpic,
            ]}
          >
            {achievement.rarity === 'rare' && achievement.unlocked && (
              <View style={styles.rarityBadge}>
                <Text style={styles.rarityBadgeText}>RARA</Text>
              </View>
            )}
            {achievement.rarity === 'epic' && achievement.unlocked && (
              <View style={[styles.rarityBadge, styles.rarityEpic]}>
                <Text style={styles.rarityBadgeText}>ÉPICA</Text>
              </View>
            )}
            <View style={styles.achievementIconWrapper}>
              <Text style={styles.achievementIcon}>
                {achievement.unlocked ? achievement.icon : '🔒'}
              </Text>
            </View>
            <Text style={[styles.achievementName, { color: dynamicColors.textPrimary }]}>{achievement.name}</Text>
            <Text style={[styles.achievementDesc, { color: dynamicColors.textSecondary }]}>{achievement.desc}</Text>
            {achievement.unlocked ? (
              <Text style={[styles.achievementDate, { color: dynamicColors.textTertiary }]}>{achievement.date}</Text>
            ) : (
              <View style={styles.achievementProgressContainer}>
                <View style={[styles.achievementProgressBar, { backgroundColor: dynamicColors.bgTertiary }]}>
                  {/* Clamp + fallback 0: dado do banco pode vir sem progress */}
                  <View style={[styles.achievementProgressFill, { width: `${Math.min(100, Math.max(0, Number(achievement.progress) || 0))}%` }]} />
                </View>
                <Text style={[styles.achievementProgressText, { color: dynamicColors.textSecondary }]}>{achievement.current ?? 0}/{achievement.target ?? 0}</Text>
              </View>
            )}
            {achievement.percentage && (
              <Text style={[styles.achievementPercentage, { color: dynamicColors.textTertiary }]}>Apenas {achievement.percentage}% têm</Text>
            )}
          </View>
          ))
        )}
      </ScrollView>
    </View>
  );

  // Feed de Atividades
  const ActivityFeed = () => (
    <View style={styles.activitySection}>
      <Text style={[styles.sectionTitle, { color: dynamicColors.textPrimary }]}>⚡ Atividades Recentes</Text>
      <View style={[styles.activityList, {
        backgroundColor: isDarkMode ? 'transparent' : dynamicColors.bgCard,
        borderColor: isDarkMode ? 'transparent' : dynamicColors.border,
        borderWidth: isDarkMode ? 0 : 1,
        borderRadius: isDarkMode ? 0 : 12,
      }]}>
        {actLoading ? (
          [0, 1, 2].map((i) => (
            <View
              key={`act-sk-${i}`}
              style={[styles.activityItem, { backgroundColor: dynamicColors.bgTertiary, borderColor: dynamicColors.border }]}
            >
              <SkeletonCircle size={40} style={skeletonTint} />
              <View style={[styles.activityContent, { marginLeft: 12 }]}>
                <SkeletonText width={180} height={13} style={skeletonTint} />
                <SkeletonText width={70} height={10} style={[skeletonTint, { marginTop: 6 }]} />
              </View>
            </View>
          ))
        ) : (
          activities.map((activity) => (
          <View key={activity.id} style={[styles.activityItem, {
            backgroundColor: dynamicColors.bgTertiary,
            borderColor: dynamicColors.border,
          }]}>
            <View style={[
              styles.activityIcon,
              activity.type === 'quiz' && styles.activityIconSuccess,
              activity.type === 'achievement' && styles.activityIconAchievement,
              activity.type === 'comment' && styles.activityIconComment,
              activity.type === 'streak' && styles.activityIconStreak,
            ]}>
              <Text style={styles.activityEmoji}>{activity.icon}</Text>
            </View>
            <View style={styles.activityContent}>
              <Text style={[styles.activityText, { color: dynamicColors.textPrimary }]}>{activity.text}</Text>
              <Text style={[styles.activityTime, { color: dynamicColors.textTertiary }]}>{activity.time}</Text>
            </View>
            {activity.badge && (
              <View style={[
                styles.activityBadge,
                activity.badgeType === 'success' && styles.activityBadgeSuccess,
                activity.badgeType === 'achievement' && styles.activityBadgeAchievement,
                activity.badgeType === 'streak' && styles.activityBadgeStreak,
              ]}>
                <Text style={[styles.activityBadgeText, { color: dynamicColors.textPrimary }]}>{activity.badge}</Text>
              </View>
            )}
          </View>
          ))
        )}
      </View>
      <TouchableOpacity style={[styles.loadMoreBtn, { borderColor: dynamicColors.border }]}>
        <Text style={[styles.loadMoreText, { color: dynamicColors.accentPrimary }]}>Carregar Mais Atividades</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <DottedBackground>
      <View style={[styles.container, { backgroundColor: 'transparent' }]}>
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        removeClippedSubviews={true}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[dynamicColors.accentPrimary]}
            tintColor={dynamicColors.accentPrimary}
          />
        }
      >
        {ProfileHeader()}

        {activeTab === 'posts' ? (
          <View style={[styles.content, { backgroundColor: 'transparent' }]}>
            <UserContentList userId={userId} isOwner colors={dynamicColors} />
          </View>
        ) : (
          <View style={[styles.content, { backgroundColor: 'transparent' }]}>
            {WeeklyPerformanceCard()}
            <BenchmarkThermometerCard
              position={234}
              totalCandidates={4521}
              percentile={5.2}
              weeklyChange={12}
              userPhoto={profile?.foto_url}
              onPress={handleNavigateToBenchmark}
              isDarkMode={isDarkMode}
            />
            {StatsCards()}
            {NextGoalCard()}
            {AchievementsGrid()}
            {ActivityFeed()}
          </View>
        )}
        <View style={styles.bottomPadding} />
      </ScrollView>

      {/* Modal de Edição de Perfil */}
      <Modal
        visible={editModalMounted}
        animationType="none"
        transparent={true}
        statusBarTranslucent
        onRequestClose={handleCloseEditModal}
      >
        <View style={styles.modalOverlay}>
          {/* Camada escura: IRMÃ da folha, não o container — assim faz fade
              parada enquanto a folha desliza (ver comentário na animação). */}
          <Animated.View
            style={[styles.modalDim, { opacity: editFadeAnim }]}
            pointerEvents="none"
          />
          {/* Área acima da folha: fecha ao tocar fora.
              Chama o MESMO `handleCloseEditModal` do botão X — então tocar fora
              e fechar pelo X têm exatamente o mesmo efeito, inclusive apagar a
              foto órfã que ainda não foi salva (`pendingPhotoRef`).
              Bloqueado durante o salvamento: fechar no meio de um upload
              deixaria a tela num estado ambíguo. */}
          <Pressable
            style={styles.modalBackdrop}
            onPress={isSaving ? undefined : handleCloseEditModal}
            disabled={isSaving}
            accessibilityRole="button"
            accessibilityLabel="Fechar edição de perfil"
          />
          <Animated.View
            style={[
              styles.modalContainer,
              { backgroundColor: dynamicColors.bgSecondary, transform: [{ translateY: editSlideAnim }] },
            ]}
          >
            <View style={[styles.modalHeader, { borderBottomColor: dynamicColors.border }]}>
              <TouchableOpacity onPress={handleCloseEditModal}>
                <Ionicons name="close" size={22} color={dynamicColors.textSecondary} />
              </TouchableOpacity>
              <Text style={[styles.modalTitle, { color: dynamicColors.textPrimary }]}>Editar Perfil</Text>
              <View style={{ width: 24 }} />
            </View>

            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {/* Preview atual e opção de foto */}
              <Text style={[styles.inputLabel, { color: dynamicColors.textSecondary }]}>Sua Foto ou Avatar</Text>
              <View style={[styles.photoSection, { backgroundColor: dynamicColors.bgTertiary }]}>
                <View style={[styles.photoPreview, { backgroundColor: dynamicColors.bgPrimary }]}>
                  <EditPhotoPreview fotoUrl={editForm.fotoUrl} avatarSeed={editForm.avatarSeed} />
                </View>
                <View style={styles.photoButtons}>
                  <TouchableOpacity style={styles.photoBtn} onPress={handlePickPhoto} disabled={isSaving}>
                    <Text style={styles.photoBtnText}>{editForm.fotoUrl ? 'Trocar Foto' : 'Adicionar Foto'}</Text>
                  </TouchableOpacity>
                  {editForm.fotoUrl && (
                    <TouchableOpacity style={styles.photoRemoveBtn} onPress={handleRemovePhoto}>
                      <Ionicons name="trash-outline" size={14} color="#FF6B6B" style={{ marginRight: 4 }} /><Text style={styles.photoRemoveBtnText}>Remover</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              {/* Seletor de Avatar */}
              {!editForm.fotoUrl && (
                <>
                  <Text style={[styles.inputLabel, { color: dynamicColors.textSecondary }]}>Ou escolha um Avatar</Text>
                  <View style={styles.avatarGrid}>
                    {FUN_EMOJI_SEEDS.map((seed) => (
                      <AvatarGridItem
                        key={seed}
                        seed={seed}
                        selected={editForm.avatarSeed === seed}
                        onSelect={handleSelectAvatar}
                        bgColor={dynamicColors.bgTertiary}
                      />
                    ))}
                  </View>
                </>
              )}

              {/* Campo Nome */}
              <Text style={[styles.inputLabel, { color: dynamicColors.textSecondary }]}>Nome</Text>
              <TextInput
                style={[styles.textInput, { backgroundColor: dynamicColors.bgTertiary, borderColor: dynamicColors.border, color: dynamicColors.textPrimary }]}
                value={editForm.name}
                onChangeText={(text) => setEditForm(prev => ({ ...prev, name: text }))}
                placeholder="Seu nome"
                placeholderTextColor={dynamicColors.textTertiary}
              />

              {/* Campo Username */}
              <Text style={[styles.inputLabel, { color: dynamicColors.textSecondary }]}>Username</Text>
              <View style={[styles.usernameInputContainer, { backgroundColor: dynamicColors.bgTertiary, borderColor: dynamicColors.border }]}>
                <Text style={[styles.usernamePrefix, { color: dynamicColors.textSecondary }]}>@</Text>
                <TextInput
                  style={[styles.usernameInput, { color: dynamicColors.textPrimary }]}
                  value={editForm.username}
                  onChangeText={(text) => setEditForm(prev => ({ ...prev, username: text.replace(/[^a-zA-Z0-9_]/g, '') }))}
                  placeholder="seu_username"
                  placeholderTextColor={dynamicColors.textTertiary}
                  autoCapitalize="none"
                />
              </View>

              {/* Campo Bio */}
              <Text style={[styles.inputLabel, { color: dynamicColors.textSecondary }]}>Bio</Text>
              <TextInput
                style={[styles.textInput, styles.bioInput, { backgroundColor: dynamicColors.bgTertiary, borderColor: dynamicColors.border, color: dynamicColors.textPrimary }]}
                value={editForm.bio}
                onChangeText={(text) => setEditForm(prev => ({ ...prev, bio: text }))}
                placeholder="Conte um pouco sobre você..."
                placeholderTextColor={dynamicColors.textTertiary}
                multiline
                numberOfLines={2}
                maxLength={60}
              />
              <Text style={[styles.charCount, { color: dynamicColors.textTertiary }]}>{editForm.bio?.length || 0}/60</Text>

              {/* Botão Salvar */}
              <TouchableOpacity
                style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                onPress={handleSaveProfile}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <><Ionicons name="checkmark-circle-outline" size={18} color="#FFFFFF" style={{ marginRight: 6 }} /><Text style={styles.saveButtonText}>Salvar Alterações</Text></>
                )}
              </TouchableOpacity>

              {/* Zona de perigo: exclusão de conta */}
              <TouchableOpacity
                style={styles.deleteAccountButton}
                onPress={() => setIsDeleteModalVisible(true)}
                disabled={isSaving}
              >
                <Ionicons name="trash-outline" size={16} color="#E53935" style={{ marginRight: 6 }} />
                <Text style={styles.deleteAccountButtonText}>Excluir minha Conta</Text>
              </TouchableOpacity>
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      {/* Modal de confirmação de exclusão de conta (com senha) */}
      <Modal
        visible={isDeleteModalVisible}
        animationType="fade"
        transparent={true}
        onRequestClose={() => { if (!isDeleting) setIsDeleteModalVisible(false); }}
      >
        <View style={styles.deleteModalOverlay}>
          <View style={[styles.deleteModalCard, { backgroundColor: dynamicColors.bgSecondary }]}>
            <Ionicons name="warning-outline" size={36} color="#E53935" style={{ alignSelf: 'center', marginBottom: 8 }} />
            <Text style={[styles.deleteModalTitle, { color: dynamicColors.textPrimary }]}>Excluir minha conta</Text>
            <Text style={[styles.deleteModalMessage, { color: dynamicColors.textSecondary }]}>
              Esta ação é permanente e irreversível. Sua conta e todos os seus dados (perfil, posts, comentários, progresso) serão apagados. Digite sua senha para confirmar.
            </Text>
            <TextInput
              style={[styles.textInput, { backgroundColor: dynamicColors.bgTertiary, borderColor: dynamicColors.border, color: dynamicColors.textPrimary }]}
              value={deletePassword}
              onChangeText={setDeletePassword}
              placeholder="Sua senha"
              placeholderTextColor={dynamicColors.textTertiary}
              secureTextEntry
              autoCapitalize="none"
              editable={!isDeleting}
            />
            <View style={styles.deleteModalActions}>
              <TouchableOpacity
                style={[styles.deleteModalCancelBtn, { borderColor: dynamicColors.border }]}
                onPress={() => { setIsDeleteModalVisible(false); setDeletePassword(''); }}
                disabled={isDeleting}
              >
                <Text style={[styles.deleteModalCancelText, { color: dynamicColors.textSecondary }]}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.deleteModalConfirmBtn, isDeleting && styles.saveButtonDisabled]}
                onPress={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.deleteModalConfirmText}>Excluir</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Feedback estilizado do fluxo de foto (sobre o modal de edição) */}
      <FeedbackModal
        visible={!!feedback}
        variant={feedback?.variant}
        title={feedback?.title}
        message={feedback?.message}
        onClose={() => setFeedback(null)}
      />
      </View>
    </DottedBackground>
  );
};

export default ProfileScreen;
